<template>
  <panel-layout
    id="segment-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="segment" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ segment.isNewRecord ? $t('segment.new') : segment.name }}
          </h2>
          <div v-if="!segment.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.segment.edit.id', { id: segment.idsegment })" />
          </div>
        </div>
      </div>
    </template>

    <template #actions-right>
      <div v-if="segment" class="flex items-center space-x-2">
        <o-history-link :item="segment" model="Segment" icon="history" slim flat />
      </div>
    </template>

    <o-form v-if="segment" ref="form" class="h-full w-full">
      <o-scroll class="h-full">
        <div class="w-full px-6 py-4">
          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('general.title')" :info="$t('pages.segment.edit.label')">
              <text-field v-model="segment.name" :label="$t('general.title')" :status="status" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" class="w-full" rules="required" :label="$t('general.shortName')" :info="$t('pages.segment.edit.shortNameInfo')">
              <text-field v-model="segment.shortName" :label="$t('general.shortName')" :status="status" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group :label="$t('general.customerVisible')" :name="$t('general.customerVisible')" :info="$t('pages.segment.edit.customerVisible')" class="block pb-3">
              <toggle-field v-model="segment.customerVisible" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group
              class="w-full"
              rules="required"
              :label="$t('general.colour')"
              :name="$t('general.colour')"
              :info="$t('pages.segment.edit.colourInfo')"
              :validation-value="segment.colour"
            >
              <div class="flex flex-wrap -mx-2">
                <div
                  v-for="(colour, key) in colours"
                  :key="key"
                  class="p-2 rounded-xl relative transform transition hover:scale-104 cursor-pointer"
                  :style="{
                    backgroundColor: segment.colour === Number(key) ? `#${colour}2e` : '',
                  }"
                  @click="segment.colour = Number(key)"
                >
                  <div
                    :style="{
                      backgroundColor: '#' + colour,
                    }"
                    class="w-10 h-10 rounded-lg border"
                  />

                  <o-icon
                    v-if="segment.colour === key"
                    icon="check"
                    class="hidden top-1/2 left-1/2 absolute transform -translate-x-1/2 -translate-y-1/2"
                    fill
                    :style="{
                      color: '#' + colour
                    }"
                  />
                </div>
              </div>
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('pages.segment.edit.onlinePaymentCapture')" :info="$t('pages.segment.edit.onlinePaymentCaptureInfo')">
              <segment-field v-model="segment.onlinePaymentCaptureRequired" :options="segmentOnlinePaymentCaptureRequiredOptions" :label="$t('pages.segment.edit.onlinePaymentCapture')" :status="status" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" class="w-full" :label="$t('pages.segment.edit.onlinePaymentDeposit')" :info="$t('pages.segment.edit.onlinePaymentDepositInfo')">
              <text-field v-model="_onlinePaymentDepositRequired" :label="$t('pages.segment.edit.onlinePaymentDeposit')" :status="status" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('pages.segment.edit.onlineBookingAllowed')" :info="$t('pages.segment.edit.onlineBookingAllowedInfo')">
              <segment-field v-model="segment.onlineBookingAllowed" :options="segmentOnlineBookingAllowedOptions" :label="$t('pages.segment.edit.onlineBookingAllowed')" :status="status" />
            </o-field-group>
          </div>

          <div class="flex space-x-4 mb-6">
            <o-field-group v-slot="{ status }" class="w-full" :label="$t('pages.segment.edit.onlineBookingMessage')" :info="$t('pages.segment.edit.onlineBookingMessageInfo')">
              <text-field v-model="segment.onlineBookingMessage" :label="$t('pages.segment.edit.onlineBookingMessage')" :status="status" />
            </o-field-group>
          </div>
        </div>
      </o-scroll>
    </o-form>

    <template #footer>
      <div v-if="segment">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import {
  TextField,
  SegmentField,
  ColourField,
  ToggleField,
} from '@/components/fields'

import {
  SegmentOnlinePaymentCaptureRequired,
  SegmentOnlinePaymentCaptureRequiredLabels,
  SegmentOnlineBookingAllowed,
  SegmentOnlineBookingAllowedLabels,
} from '~/_api/models/segment'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const segment = ref<any>(null)
const loading = ref(true)
const colours: Record<number, string> = {
  1: '999999',
  2: '468847',
  3: 'F89406',
  4: 'B94A48',
  5: '3A87AD',
  6: '333333',
}

const confirmDialog = ref<any>()
const form = ref<any>()

const _colour = computed({
  get () {
    return '#' + segment.value.hexColour
  },
  set (value: any) {
    segment.value.hexColour = value.replace('#', '')
  },
})

const segmentOnlinePaymentCaptureRequiredOptions = computed(() => {
  return Object.values(SegmentOnlinePaymentCaptureRequired)
    .filter((value): value is SegmentOnlinePaymentCaptureRequired => value === SegmentOnlinePaymentCaptureRequired.system || typeof value === 'number')
    .map((value) => {
      return {
        label: $i18n.t(SegmentOnlinePaymentCaptureRequiredLabels[value]!),
        value,
      }
    })
})

const segmentOnlineBookingAllowedOptions = computed(() => {
  return Object.values(SegmentOnlineBookingAllowed)
    .filter((value): value is SegmentOnlineBookingAllowed => typeof value === 'number')
    .map((value) => {
      return {
        label: $i18n.t(SegmentOnlineBookingAllowedLabels[value]),
        value,
      }
    })
})

const _onlinePaymentDepositRequired = computed({
  get () {
    return segment.value.onlinePaymentDepositRequired
  },
  set (value: any) {
    segment.value.onlinePaymentDepositRequired = value || null
  },
})

const { unsavedChanges, snapshot } = useUnsavedChanges(segment)

const save = async (close = false) => {
  if (!await form.value?.validate()) return false

  loading.value = true

  try {
    const _segment = (await $api.segment.save(segment.value)).data

    if (_segment) {
      segment.value = _segment
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.segment.edit.success'),
        text: $i18n.t('pages.segment.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('Segment', _segment)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }

  loading.value = false

  return false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

async function init(id?: string) {
  loading.value = true
  try {
    if (!id) {
      segment.value = (await $api.segment.defaults({} as any)).data
    } else {
      segment.value = (await $api.segment.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })
</script>
